import React, { useMemo } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { DatePicker } from 'rsuite';
import { BID_TIME_OPTIONS, bidTimeLabel, pickerDate } from '../../utils/helper/auctionForm';
import { describePrizeSplit } from '../../utils/helper/prizeSplit';
import { AuctionFitNote } from './AuctionFitNote';
import { PrizeSplitTotal } from './PrizeSplitTotal';

/*
 * The fields of the auction form, shared by Create Auction and the Update Auction modal. Each takes
 * `form`, the result of useAuctionForm, and renders its label, control and feedback only: the screen
 * puts it in its own Form.Group / Col, so each keeps its own layout.
 */

/** Letters, "+" and "-": keys a percentage never needs, which a number input would still take. */
const blockLetterKeys = (event) => {
  if (/^[a-zA-Z+-]$/.test(event.key)) event.preventDefault();
};

/** The contests a save can name (selectableContests); picking one loads its teams. */
export const ContestSelect = ({ form }) => (
  <>
    <Form.Label>Select Contest</Form.Label>
    <InputGroup hasValidation>
      <Form.Select
        name="tournamentId"
        className="select-option tournament"
        required
        value={form.values.tournamentId}
        onChange={(event) => form.pickContest(event.target.value)}
      >
        <option value="">Select Contest</option>
        {form.contests.map((contest) => (
          <option key={contest.tournamentId} value={contest.tournamentId}>
            {contest.tournamentName}
          </option>
        ))}
      </Form.Select>
      <Form.Control.Feedback type="invalid">Please select a Contest.</Form.Control.Feedback>
    </InputGroup>
  </>
);

/** The selected contest's sport, from the contest itself. */
export const SportName = ({ form }) => {
  const picked = Boolean(form.values.tournamentId);
  return (
    <>
      <p className={picked ? 'mt-3 label' : ''}>{picked ? 'Sport Name' : ''}</p>
      <p className={picked ? 'input-game' : ''}>{picked ? form.values.sportName : ''}</p>
    </>
  );
};

export const AuctionNameInput = ({ form }) => (
  <>
    <Form.Label>Auction Name</Form.Label>
    <Form.Control
      name="auctionName"
      maxLength={50}
      value={form.values.auctionName}
      onChange={(event) => form.setField('auctionName', event.target.value)}
      className="select-option tournament"
      required
      type="text"
      placeholder="Enter your Auction Name"
    />
    <Form.Control.Feedback type="invalid">Please enter Auction Name.</Form.Control.Feedback>
  </>
);

/**
 * The auction's start, in local time. Its error shows once a save has been tried without one.
 *
 * @param {{form: Object, labelClassName?: string, pickerClassName?: string}} props
 */
export const StartDatePicker = ({ form, labelClassName, pickerClassName }) => {
  const value = useMemo(() => pickerDate(form.values.startDate), [form.values.startDate]);
  const missing = form.values.validated && !form.values.startDate;
  // #309: a missing date has the red border every other field has. rsuite's picker is not a native
  // input, so Bootstrap's :invalid border never reaches it; `.Auction-Date.is-invalid` does (#108).
  const pickerClasses = [pickerClassName, missing && 'Auction-Date is-invalid'].filter(Boolean).join(' ');

  return (
    <>
      <Form.Label className={labelClassName}>Auction Start Date</Form.Label>
      <DatePicker
        format="MM/dd/yyyy hh:mm:ss aa"
        showMeridian
        placeholder="Start Date"
        value={value}
        name="startDate"
        className={pickerClasses || undefined}
        onChange={(date) => form.setField('startDate', date ?? '')}
        disabledDate={(date) => date < new Date().setHours(0, 0, 0, 0)}
        required
      />
      <Form.Control.Feedback type="invalid" style={{ display: missing ? 'block' : 'none' }}>
        Please select the start date and time.
      </Form.Control.Feedback>
    </>
  );
};

/** The bid time, with whether the lots fit before bidding closes under it (#171). */
export const BidTimeSelect = ({ form }) => (
  <>
    <Form.Label className="mt-3">Bid Time Limit</Form.Label>
    <InputGroup hasValidation>
      <Form.Select
        name="bidTime"
        value={form.values.bidTime ?? ''}
        onChange={(event) => form.setField('bidTime', event.target.value)}
        className="select-option tournament"
        required
      >
        <option value="">Select Time Limit</option>
        {BID_TIME_OPTIONS.map((minutes) => (
          <option key={minutes} value={minutes}>
            {bidTimeLabel(minutes)}
          </option>
        ))}
      </Form.Select>
      <Form.Control.Feedback type="invalid">Please add Bid Time Limit.</Form.Control.Feedback>
    </InputGroup>
    <AuctionFitNote fit={form.auctionFit} />
  </>
);

/**
 * The Manager Hold. The field shows exactly what will be checked and sent; an out-of-range value is
 * named by the running total (#146).
 *
 * @param {{form: Object, className: string}} props className is the input's, which differs by screen.
 */
export const ManagerHoldInput = ({ form, className }) => (
  <>
    <Form.Label className="mt-3">Manager Hold ( % )</Form.Label>
    <input
      name="xcuttaTake"
      value={form.values.xcuttaTake ?? ''}
      onChange={(event) => form.setField('xcuttaTake', event.target.value)}
      className={className}
      required
      type="number"
      placeholder="Enter your cut"
      onKeyDown={blockLetterKeys}
    />
    <Form.Control.Feedback type="invalid">Please enter Manager Hold.</Form.Control.Feedback>
  </>
);

const AmountInput = ({ form, name, label, placeholder, feedback }) => (
  <>
    <Form.Label>{label}</Form.Label>
    <InputGroup hasValidation>
      <Form.Control
        type="number"
        name={name}
        value={form.values[name] ?? ''}
        onChange={(event) => form.setField(name, event.target.value)}
        placeholder={placeholder}
        aria-describedby="inputGroupPrepend"
        required
      />
      <Form.Control.Feedback type="invalid">{feedback}</Form.Control.Feedback>
    </InputGroup>
  </>
);

/** Create Auction only: the Update Auction modal sends the stored value back. */
export const StartingBidInput = ({ form }) => (
  <AmountInput
    form={form}
    name="startingBid"
    label="Starting Bid"
    placeholder="Enter your Starting Bid"
    feedback="Please enter a starting bid."
  />
);

/** Create Auction only: the Update Auction modal sends the stored value back. */
export const BidIncrementInput = ({ form }) => (
  <AmountInput
    form={form}
    name="bidIncrement"
    label="Minimum Bid Increment"
    placeholder="Enter your Bid Increment"
    feedback="Please enter a  bid increment."
  />
);

/**
 * The prize split's winner places: the share they divide, the running total, how many there are and
 * each one's percentage. Always shown, with the running total above the count (#146): the block
 * used to be hidden once the Manager Hold reached 100%, which left no way to pick the winners and
 * nothing saying why. Changing the count keeps what was entered for the places that stay.
 */
export const WinnerFields = ({ form }) => {
  const { teams, winners } = form.values;

  return (
    <>
      <p className="winner-distribution">
        Winner Prize Distribution {describePrizeSplit(form.prizeSplit).winnersTarget} %
      </p>
      <PrizeSplitTotal summary={form.prizeSplit} placeLabels={form.winnerTitles} />
      <Form.Label>Total Winners</Form.Label>
      <Form.Select
        name="winnerCount"
        value={winners.length || ''}
        disabled={teams.length === 0}
        // "Select Winner Count" is the empty option, which parses to NaN.
        onChange={(event) => form.setWinnerCount(parseInt(event.target.value, 10) || 0)}
        className="select-option tournament"
        required
      >
        <option value="">Select Winner Count</option>
        {teams.map((_, index) => (
          <option key={index + 1} value={index + 1}>
            {index + 1}
          </option>
        ))}
      </Form.Select>

      {winners.length > 0 && (
        <div className="winner-percentage">
          <Row>
            {winners.map((winner, index) => (
              <Col md={12} key={index}>
                <div>
                  <input
                    type="text"
                    className="input-editable"
                    name={`winnerName-${index}`}
                    placeholder={`Place ${index + 1}`}
                    value={winner.title}
                    disabled
                  />
                  <input
                    name={`winnerPercentage-${index}`}
                    className="select-option tournament xcuttaTakeinput mb-2"
                    placeholder="Enter Percentage"
                    type="number"
                    value={winner.percentage ?? ''}
                    onChange={(event) => form.setWinnerPercentage(index, event.target.value)}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </>
  );
};

/**
 * Create Auction only: whether the auction has a secondary market, and whether it is public or
 * private. The Update Auction modal leaves both as stored (WEB-P3-159).
 */
export const SecondaryMarketFields = ({ form }) => (
  <>
    <label htmlFor="market" className="input-check-group mt-3">
      <input
        type="checkbox"
        id="market"
        name="market"
        className="inputCheck"
        checked={form.values.secondaryMarket}
        onChange={(event) => form.setSecondaryMarket(event.target.checked)}
      />
      Secondary Market
    </label>
    {form.values.secondaryMarket ? (
      <Form.Group as={Col} className="mt-3" controlId="secondaryMarketType">
        <Form.Label>Types of Secondary Market</Form.Label>
        <InputGroup hasValidation>
          <Form.Select
            name="secondaryMarket"
            value={form.values.marketType}
            onChange={(event) => form.setField('marketType', event.target.value)}
            className="select-option tournament"
          >
            <option value="">Select an option</option>
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </Form.Select>
        </InputGroup>
      </Form.Group>
    ) : (
      ''
    )}
  </>
);
